import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink, Loader2, LogOut, Package, Plus, RefreshCw, Save, Trash2, Upload } from 'lucide-react';
import AdminTabs from './AdminTabs';
import BrandMark from '../../components/BrandMark';
import {
  adminAuthorizationList,
  adminCreateAuthorization,
  adminCoursewareList,
  adminCreateCourseware,
  adminCreatePackage,
  adminDeleteCourseware,
  adminDeletePackage,
  adminReorderCourseware,
  adminReorderPackages,
  adminUpdateAuthorization,
  adminUpdateCourseware,
  adminUpdatePackage,
  checkAuth,
  clearToken,
} from '../../admin/api';

const L = {
  title: '课件管理',
  subtitle: '上传或替换课件包，机构端统一通过受控播放器打开，并自动生成授权号与右下角水印。',
  newTitle: '新增网页课件',
  listTitle: '已有课件',
  courseTitle: '课件标题',
  desc: '课件说明',
  file: 'HTML/HTM/ZIP 文件',
  upload: '上传新课件',
  replace: '替换 HTML/ZIP 文件',
  save: '保存修改',
  published: '已发布',
  draft: '草稿',
  logout: '退出',
  viewSite: '看网站',
  refresh: '刷新',
  protectionTitle: '交付防护策略',
  protectionDesc: '课件不直接暴露原始入口；前台统一走受控播放器，右下角显示品牌水印和授权号，后台记录打开时间、账号、设备和追踪码。',
  controlled: '受控播放器',
  cornerWatermark: '右下角品牌水印',
  authNumber: '授权号追踪',
  authTitle: '机构授权账号',
  authDesc: '机构老师用机构授权码和手机号登录课程系统。勾选下方课程包即可为该机构开放对应课程，不同授权码可开放不同课程包。',
  orgCode: '机构授权码',
  tenantName: '机构名称',
  teacherName: '老师姓名',
  phone: '手机号',
  addAuth: '新增授权账号',
  active: '启用',
  disabled: '停用',
  authPackages: '开放课程包',
  packageTitle: '课程包管理',
  packageDesc: '一个课程包包含多节课程。机构按课程包授权，包内课程整体开放。',
  newPackage: '新增课程包',
  packageName: '课程包名称',
  noPackage: '独立课程（不归入课程包）',
  belongPackage: '所属课程包',
  deleteCourse: '删除',
  moveUp: '上移',
  moveDown: '下移',
  emptyPackage: '（暂无课程）',
};

function Header({ onLogout }) {
  return (
    <header className="sticky top-0 z-20 border-b border-gray-200 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3">
        <div className="flex items-center gap-2.5">
          <BrandMark size="sm" />
          <div className="font-bold text-slate-900 max-sm:hidden">加太教育 · {L.title}</div>
        </div>
        <AdminTabs />
        <div className="flex items-center gap-2">
          <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 max-sm:hidden"><ExternalLink className="h-4 w-4" /> {L.viewSite}</a>
          <button onClick={onLogout} className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100"><LogOut className="h-4 w-4" /> {L.logout}</button>
        </div>
      </div>
    </header>
  );
}

export default function CoursewareAdmin() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [newForm, setNewForm] = useState({ title: '', description: '', packageId: '', importMode: 'lesson', file: null });
  const [newPackage, setNewPackage] = useState({ title: '', description: '' });
  const [authItems, setAuthItems] = useState([]);
  const [authSearch, setAuthSearch] = useState({ keyword: '', searched: false });
  const [authForm, setAuthForm] = useState({ orgCode: '', tenantName: '', teacherName: '', phone: '', status: 'active', packageIds: [] });

  useEffect(() => {
    (async () => {
      const ok = await checkAuth();
      if (!ok) { nav('/admin/login'); return; }
      await load();
    })();
    // Only run this auth bootstrap on page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  const flash = (message) => { setToast(message); setTimeout(() => setToast(''), 2400); };
  const logout = () => { clearToken(); nav('/admin/login'); };

  async function load() {
    setLoading(true);
    try {
      const coursewareData = await adminCoursewareList();
      setItems(coursewareData.items || []);
      setPackages(coursewareData.packages || []);
    } catch (err) {
      flash(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function createPackage(event) {
    event.preventDefault();
    if (!newPackage.title.trim()) { flash('请填写课程包名称'); return; }
    setSaving(true);
    try {
      await adminCreatePackage(newPackage);
      setNewPackage({ title: '', description: '' });
      await load();
      flash('课程包已创建');
    } catch (err) { flash(err.message); } finally { setSaving(false); }
  }

  async function savePackage(pkg) {
    setSaving(true);
    try {
      await adminUpdatePackage(pkg.id, { title: pkg.title, description: pkg.description, status: pkg.status });
      await load();
      flash('课程包已更新');
    } catch (err) { flash(err.message); } finally { setSaving(false); }
  }

  async function removePackage(pkg) {
    if (!window.confirm(`确定删除课程包"${pkg.title}"？包内所有课程也会一并删除，且无法恢复。`)) return;
    setSaving(true);
    try {
      await adminDeletePackage(pkg.id);
      await load();
      flash('课程包已删除');
    } catch (err) { flash(err.message); } finally { setSaving(false); }
  }

  function updatePackageItem(id, patch) {
    setPackages((prev) => prev.map((pkg) => pkg.id === id ? { ...pkg, ...patch } : pkg));
  }

  async function movePackage(index, delta) {
    const next = [...packages];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setPackages(next);
    setSaving(true);
    try { await adminReorderPackages(next.map((p) => p.id)); } catch (err) { flash(err.message); await load(); } finally { setSaving(false); }
  }

  async function createAuthorization(event) {
    event.preventDefault();
    if (!authForm.orgCode || !authForm.phone) { flash('请填写机构授权码和手机号'); return; }
    setSaving(true);
    try {
      await adminCreateAuthorization(authForm);
      setAuthForm({ orgCode: '', tenantName: '', teacherName: '', phone: '', status: 'active', packageIds: [] });
      await load();
      flash('授权账号已保存');
    } catch (err) {
      flash(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function saveAuthorization(item) {
    setSaving(true);
    try {
      await adminUpdateAuthorization(item.userId, item);
      await load();
      flash('授权账号已更新');
    } catch (err) {
      flash(err.message);
    } finally {
      setSaving(false);
    }
  }

  function updateAuthItem(userId, patch) {
    setAuthItems((prev) => prev.map((item) => item.userId === userId ? { ...item, ...patch } : item));
  }

  function toggleAuthPackage(userId, packageId) {
    setAuthItems((prev) => prev.map((item) => {
      if (item.userId !== userId) return item;
      const set = new Set(item.packageIds || []);
      if (set.has(packageId)) set.delete(packageId); else set.add(packageId);
      return { ...item, packageIds: [...set] };
    }));
  }

  function toggleNewAuthPackage(packageId) {
    setAuthForm((prev) => {
      const set = new Set(prev.packageIds || []);
      if (set.has(packageId)) set.delete(packageId); else set.add(packageId);
      return { ...prev, packageIds: [...set] };
    });
  }
  async function searchAuthorization(event) {
    event.preventDefault();
    const keyword = authSearch.keyword.trim();
    if (!keyword) {
      setAuthItems([]);
      setAuthSearch((prev) => ({ ...prev, searched: false }));
      flash('\u8bf7\u8f93\u5165\u673a\u6784\u6388\u6743\u7801\u6216\u673a\u6784\u540d\u79f0');
      return;
    }
    setSaving(true);
    try {
      const data = await adminAuthorizationList(keyword);
      setAuthItems(data.items || []);
      setAuthSearch((prev) => ({ ...prev, searched: true }));
      if (!(data.items || []).length) flash('\u672a\u627e\u5230\u5339\u914d\u7684\u6388\u6743\u8d26\u53f7');
    } catch (err) {
      flash(err.message);
    } finally {
      setSaving(false);
    }
  }


  async function createCourseware(event) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const fileInput = formEl.querySelector('input[type="file"]');
    const file = fileInput?.files?.[0] || newForm.file;

    if (!file) { flash('请选择 HTML 或 ZIP 课件包'); return; }
    if (newForm.importMode === 'package' && !newForm.packageId) { flash('请选择要导入的课程包'); return; }

    try {
      const result = await adminCreateCourseware({ ...newForm, file });
      setNewForm({ title: '', description: '', packageId: newForm.packageId, importMode: newForm.importMode, file: null });
      formEl.reset();
      await load();
      flash(result.imported ? `已导入 ${result.imported} 节课` : '课件已上传');
    } catch (err) {
      flash(err.message || '上传失败');
    }
  }
  async function saveItem(item, event) {
    event.preventDefault();
    setSaving(true);
    try {
      await adminUpdateCourseware(item.id, item);
      await load();
      flash('课件已保存');
    } catch (err) {
      flash(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function removeItem(item) {
    if (!window.confirm(`确定删除课程"${item.title}"？此操作会删除课件文件，且无法恢复。`)) return;
    setSaving(true);
    try {
      await adminDeleteCourseware(item.id);
      await load();
      flash('课程已删除');
    } catch (err) { flash(err.message); } finally { setSaving(false); }
  }

  function updateItem(id, patch) {
    setItems((prev) => prev.map((item) => item.id === id ? { ...item, ...patch } : item));
  }

  // 按课程包对课程分组展示：每个包一组 + 独立课程一组
  function coursesInGroup(packageId) {
    return items.filter((item) => (item.packageId || '') === packageId);
  }

  async function moveCourse(groupId, index, delta) {
    const group = coursesInGroup(groupId);
    const target = index + delta;
    if (target < 0 || target >= group.length) return;
    const reordered = [...group];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    // 用新顺序覆盖 items 中该组的排列
    const others = items.filter((item) => (item.packageId || '') !== groupId);
    setItems([...others, ...reordered]);
    setSaving(true);
    try { await adminReorderCourseware(reordered.map((c) => c.id)); } catch (err) { flash(err.message); await load(); } finally { setSaving(false); }
  }

  const groups = [
    ...packages.map((pkg) => ({ id: pkg.id, title: pkg.title })),
    { id: '', title: L.noPackage },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onLogout={logout} />
      <main className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">{L.title}</h1>
            <p className="mt-2 text-sm text-gray-500">{L.subtitle}</p>
          </div>
          <button onClick={load} className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"><RefreshCw className="h-4 w-4" /> {L.refresh}</button>
        </div>

        <section className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-extrabold text-slate-900">{L.protectionTitle}</h2>
              <p className="mt-1 max-w-4xl text-sm leading-6 text-slate-600">{L.protectionDesc}</p>
            </div>
            <div className="flex flex-wrap gap-2 text-xs font-bold text-blue-700">
              <span className="rounded-full bg-white px-3 py-1 ring-1 ring-blue-100">{L.controlled}</span>
              <span className="rounded-full bg-white px-3 py-1 ring-1 ring-blue-100">{L.cornerWatermark}</span>
              <span className="rounded-full bg-white px-3 py-1 ring-1 ring-blue-100">{L.authNumber}</span>
            </div>
          </div>
        </section>

        {/* ===== 课程包管理 ===== */}
        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-soft">
          <div className="mb-4 flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-bold text-slate-900">{L.packageTitle}</h2>
              <p className="mt-1 text-sm text-gray-500">{L.packageDesc}</p>
            </div>
          </div>
          <form onSubmit={createPackage} className="mb-5 grid gap-3 md:grid-cols-[1fr_1.5fr_auto]">
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.packageName}<input className="form-input" value={newPackage.title} onChange={(e) => setNewPackage({ ...newPackage, title: e.target.value })} placeholder="如：神秘电波·六课时" /></label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.desc}<input className="form-input" value={newPackage.description} onChange={(e) => setNewPackage({ ...newPackage, description: e.target.value })} /></label>
            <div className="flex items-end"><button disabled={saving} className="btn btn-blue w-full inline-flex items-center justify-center gap-2"><Plus className="h-4 w-4" />{L.newPackage}</button></div>
          </form>
          <div className="space-y-3">
            {packages.map((pkg, index) => (
              <div key={pkg.id} className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 md:grid-cols-[auto_1fr_1.5fr_auto_auto]">
                <div className="flex items-center gap-1">
                  <button type="button" disabled={saving || index === 0} onClick={() => movePackage(index, -1)} className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30" title={L.moveUp}><ChevronUp className="h-4 w-4" /></button>
                  <button type="button" disabled={saving || index === packages.length - 1} onClick={() => movePackage(index, 1)} className="rounded-md border border-gray-200 bg-white p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30" title={L.moveDown}><ChevronDown className="h-4 w-4" /></button>
                </div>
                <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.packageName}<input className="form-input" value={pkg.title || ''} onChange={(e) => updatePackageItem(pkg.id, { title: e.target.value })} /></label>
                <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.desc}<input className="form-input" value={pkg.description || ''} onChange={(e) => updatePackageItem(pkg.id, { description: e.target.value })} /></label>
                <div className="flex items-end">
                  <select className="form-input min-w-24" value={pkg.status || 'published'} onChange={(e) => updatePackageItem(pkg.id, { status: e.target.value })}>
                    <option value="published">{L.published}</option>
                    <option value="draft">{L.draft}</option>
                  </select>
                </div>
                <div className="flex items-end gap-2">
                  <button type="button" disabled={saving} onClick={() => savePackage(pkg)} className="btn btn-blue inline-flex items-center gap-2"><Save className="h-4 w-4" />{L.save}</button>
                  <button type="button" disabled={saving} onClick={() => removePackage(pkg)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            {!packages.length && <p className="text-sm text-gray-400">暂无课程包，先创建一个课程包。</p>}
          </div>
        </section>

        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-soft">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-slate-900">{L.authTitle}</h2>
            <p className="mt-1 text-sm text-gray-500">{L.authDesc}</p>
          </div>
          <form onSubmit={createAuthorization} className="mb-5 grid gap-3">
            <div className="grid gap-3 md:grid-cols-5">
              <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.orgCode}<input className="form-input" value={authForm.orgCode} onChange={(e) => setAuthForm({ ...authForm, orgCode: e.target.value })} placeholder="demo-school" /></label>
              <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.tenantName}<input className="form-input" value={authForm.tenantName} onChange={(e) => setAuthForm({ ...authForm, tenantName: e.target.value })} /></label>
              <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.teacherName}<input className="form-input" value={authForm.teacherName} onChange={(e) => setAuthForm({ ...authForm, teacherName: e.target.value })} /></label>
              <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.phone}<input className="form-input" value={authForm.phone} onChange={(e) => setAuthForm({ ...authForm, phone: e.target.value })} /></label>
              <div className="flex items-end"><button disabled={saving} className="btn btn-blue w-full inline-flex items-center justify-center gap-2"><Save className="h-4 w-4" />{L.addAuth}</button></div>
            </div>
            {packages.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 text-sm font-medium text-slate-800">{L.authPackages}</div>
                <div className="flex flex-wrap gap-2">
                  {packages.map((pkg) => (
                    <label key={pkg.id} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${(authForm.packageIds || []).includes(pkg.id) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-slate-600'}`}>
                      <input type="checkbox" className="accent-blue-600" checked={(authForm.packageIds || []).includes(pkg.id)} onChange={() => toggleNewAuthPackage(pkg.id)} />{pkg.title}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </form>
          <form onSubmit={searchAuthorization} className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="mb-2 text-sm font-bold text-slate-800">{`\u641c\u7d22\u6388\u6743\u8d26\u53f7`}</div>
            <div className="grid gap-3 md:grid-cols-[1fr_auto]">
              <input className="form-input" value={authSearch.keyword} onChange={(e) => setAuthSearch({ keyword: e.target.value, searched: false })} placeholder={`\u8f93\u5165\u673a\u6784\u6388\u6743\u7801\u6216\u673a\u6784\u540d\u79f0`} />
              <button type="submit" disabled={saving} className="btn btn-blue px-6">{`\u641c\u7d22`}</button>
            </div>
          </form>
          {authSearch.searched && !authItems.length && <p className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">{`\u672a\u627e\u5230\u5339\u914d\u7684\u6388\u6743\u8d26\u53f7`}</p>}
          <div className="space-y-3">
            {authItems.map((item) => (
              <div key={item.userId} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_1fr_auto]">
                  <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.orgCode}<input className="form-input" value={item.orgCode || ''} disabled /></label>
                  <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.tenantName}<input className="form-input" value={item.tenantName || ''} onChange={(e) => updateAuthItem(item.userId, { tenantName: e.target.value })} /></label>
                  <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.teacherName}<input className="form-input" value={item.teacherName || ''} onChange={(e) => updateAuthItem(item.userId, { teacherName: e.target.value })} /></label>
                  <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.phone}<input className="form-input" value={item.phone || ''} onChange={(e) => updateAuthItem(item.userId, { phone: e.target.value })} /></label>
                  <div className="flex items-end gap-2">
                    <select className="form-input min-w-24" value={item.status || 'active'} onChange={(e) => updateAuthItem(item.userId, { status: e.target.value })}>
                      <option value="active">{L.active}</option>
                      <option value="disabled">{L.disabled}</option>
                    </select>
                    <button type="button" disabled={saving} onClick={() => saveAuthorization(item)} className="btn btn-blue inline-flex items-center gap-2"><Save className="h-4 w-4" />{L.save}</button>
                  </div>
                </div>
                {packages.length > 0 && (
                  <div className="mt-3">
                    <div className="mb-2 text-xs font-semibold text-slate-500">{L.authPackages}</div>
                    <div className="flex flex-wrap gap-2">
                      {packages.map((pkg) => (
                        <label key={pkg.id} className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${(item.packageIds || []).includes(pkg.id) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-slate-600'}`}>
                          <input type="checkbox" className="accent-blue-600" checked={(item.packageIds || []).includes(pkg.id)} onChange={() => toggleAuthPackage(item.userId, pkg.id)} />{pkg.title}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mb-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-soft">
          <h2 className="mb-4 text-lg font-bold text-slate-900">{L.newTitle}</h2>
          <form onSubmit={createCourseware} className="grid gap-4 md:grid-cols-[1fr_1fr]">
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{`\u4e0a\u4f20\u7c7b\u578b`}
              <select className="form-input" value={newForm.importMode} onChange={(e) => setNewForm({ ...newForm, importMode: e.target.value })}>
                <option value="lesson">{`\u5355\u8282\u8bfe`}</option>
                <option value="package">{`\u603b\u8bfe\u7a0b\u5305 ZIP\uff08\u81ea\u52a8\u62c6\u8bfe\uff09`}</option>
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.belongPackage}
              <select className="form-input" value={newForm.packageId} onChange={(e) => setNewForm({ ...newForm, packageId: e.target.value })}>
                <option value="">{newForm.importMode === 'package' ? `\u8bf7\u9009\u62e9\u5df2\u6709\u8bfe\u7a0b\u5305` : L.noPackage}</option>
                {packages.map((pkg) => <option key={pkg.id} value={pkg.id}>{pkg.title}</option>)}
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.courseTitle}<input className="form-input" value={newForm.title} disabled={newForm.importMode === 'package'} placeholder={newForm.importMode === 'package' ? `\u603b\u5305\u4e0a\u4f20\u65f6\u81ea\u52a8\u8bc6\u522b\u7b2cxx\u8282` : ''} onChange={(e) => setNewForm({ ...newForm, title: e.target.value })} /></label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.file}<input className="form-input" type="file" accept=".html,.htm,.zip,text/html,application/zip" onChange={(e) => setNewForm({ ...newForm, file: e.target.files?.[0] || null })} /></label>
            <label className="grid gap-1.5 text-sm font-medium text-slate-800 md:col-span-2">{L.desc}<textarea className="form-input min-h-20" value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} /></label>
            <div className="md:col-span-2"><button type="submit" className="btn btn-blue inline-flex items-center gap-2"><Upload className="h-4 w-4" />{newForm.importMode === 'package' ? `\u5bfc\u5165\u603b\u8bfe\u7a0b\u5305` : L.upload}</button></div>
          </form>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft">
          <h2 className="mb-4 text-lg font-bold text-slate-900">{L.listTitle}</h2>
          {loading ? <div className="flex items-center text-gray-400"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading</div> : (
            <div className="space-y-6">
              {groups.map((group) => {
                const groupCourses = coursesInGroup(group.id);
                if (!groupCourses.length && group.id !== '') return null;
                return (
                  <div key={group.id || '__ungrouped__'}>
                    <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-700">
                      <Package className="h-4 w-4 text-blue-500" />{group.title}
                      <span className="text-xs font-normal text-gray-400">{groupCourses.length ? `${groupCourses.length} 节课` : L.emptyPackage}</span>
                    </div>
                    <div className="space-y-4">
                      {groupCourses.map((item, index) => (
                        <form key={item.id} onSubmit={(event) => saveItem(item, event)} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-start gap-2">
                              <div className="flex flex-col gap-1">
                                <button type="button" disabled={saving || index === 0} onClick={() => moveCourse(group.id, index, -1)} className="rounded-md border border-gray-200 bg-white p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30" title={L.moveUp}><ChevronUp className="h-4 w-4" /></button>
                                <button type="button" disabled={saving || index === groupCourses.length - 1} onClick={() => moveCourse(group.id, index, 1)} className="rounded-md border border-gray-200 bg-white p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-30" title={L.moveDown}><ChevronDown className="h-4 w-4" /></button>
                              </div>
                              <div>
                                <div className="text-xs font-mono text-gray-400">{item.id} · {item.type} · {item.originalFilename || 'no file'}</div>
                                <div className="mt-2 flex flex-wrap gap-2 text-xs font-bold text-slate-600">
                                  <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-200">{L.controlled}</span>
                                  <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-200">{L.cornerWatermark}</span>
                                  <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-200">{L.authNumber}</span>
                                </div>
                              </div>
                            </div>
                            <select className="form-input max-w-32" value={item.status} onChange={(e) => updateItem(item.id, { status: e.target.value })}>
                              <option value="published">{L.published}</option>
                              <option value="draft">{L.draft}</option>
                            </select>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.courseTitle}<input className="form-input" value={item.title || ''} onChange={(e) => updateItem(item.id, { title: e.target.value })} /></label>
                            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.belongPackage}
                              <select className="form-input" value={item.packageId || ''} onChange={(e) => updateItem(item.id, { packageId: e.target.value })}>
                                <option value="">{L.noPackage}</option>
                                {packages.map((pkg) => <option key={pkg.id} value={pkg.id}>{pkg.title}</option>)}
                              </select>
                            </label>
                            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.replace}<input className="form-input" type="file" accept=".html,.htm,.zip,text/html,application/zip" onChange={(e) => updateItem(item.id, { file: e.target.files?.[0] || null })} /></label>
                            <label className="grid gap-1.5 text-sm font-medium text-slate-800">{L.desc}<textarea className="form-input min-h-20" value={item.description || ''} onChange={(e) => updateItem(item.id, { description: e.target.value })} /></label>
                          </div>
                          <div className="mt-3 flex items-center gap-2">
                            <button disabled={saving} className="btn btn-blue inline-flex items-center gap-2"><Save className="h-4 w-4" />{L.save}</button>
                            <button type="button" disabled={saving} onClick={() => removeItem(item)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" />{L.deleteCourse}</button>
                          </div>
                        </form>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>
      {toast && <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg">{toast}</div>}
    </div>
  );
}
